'use client';

import { useEffect, useRef } from 'react';

import Image from 'next/image';
import { usePathname, useSearchParams } from 'next/navigation';

import hamburgerMenu from '@/assets/icons/burger-menu-v2.svg';
import { LoadingIcon } from '@/components/svg';
import { useDebouncedValue } from '@/helpers/hooks/useDebounced';
import cn from '@/helpers/utils/classNames';
import { useAppDispatch, useAppSelector } from '@/store';
import { setIsMobileMenuOpen } from '@/store/slices/appConfig';
import { SearchOutlined } from '@mui/icons-material';

import Button from '../../Button';
import Container from '../../Container';
import TextInput from '../../TextInput';
import AutoCompleteWrapper from './AutoCompleteWrapper';
import { useSearchContext } from './SearchBarProvider';

export default function SearchBar() {
  const {
    value,
    onSearchChange,
    onSearch,
    autocompleteState,
    getInputProps,
    closeAutoCompleteMenu,
    isSearchBarFocused,
    setSearchBarFocused,
    setSearchValue,
    addSearchQueryToRecentSearches,
  } = useSearchContext();
  const search = useAppSelector(store => store.slices.searchSlice);
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const params = useSearchParams();

  const searchBarContainerRef = useRef<HTMLDivElement | null>(null);
  const searchBarInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const { onChange, onCompositionEnd, onFocus, type, onKeyDown, onClick, ...inputProps } = getInputProps({
    inputElement: null,
  });

  useEffect(() => {
    if (pathname.includes('products/search')) {
      const query = params.get('query');
      setSearchValue(query ?? '');
    } else {
      setSearchValue('');
    }

    setSearchBarFocused(false);
  }, [pathname, params]);

  const debouncedValue = useDebouncedValue(value, 500);
  useEffect(() => {
    onChange({
      target: { value: debouncedValue },
      currentTarget: { value: debouncedValue },
    } as unknown as Event);
  }, [debouncedValue]);

  // useDetectClickOutside(() => setSearchBarFocused(false), searchBarContainerRef);
  // useScrollListener(() => {
  //   if (searchBarInputRef && searchBarInputRef?.current) {
  //     searchBarInputRef?.current?.blur();
  //   }
  //   setSearchBarFocused(false);
  // });

  return (
    <div className="v2-bg-[#f3f6f5] v2-py-2">
      <Container className="sm:v2-px-0 xl:v2-px-0 lg:v2-px-0 v2-py-0 xs:!v2-px-3">
        <div
          className="v2-flex v2-flex-row v2-items-center sm:v2-justify-between md:v2-justify-between lg:v2-justify-end xs:v2-justify-between xxs:v2-justify-between"
          // onBlur={() => setSearchBarFocused(false)}
          ref={searchBarContainerRef}
        >
          <span
            className="v2-h-6 v2-w-6 v2-cursor-pointer sm:v2-block md:v2-block lg:v2-hidden xs:v2-block xxs:v2-block"
            onClick={() => dispatch(setIsMobileMenuOpen(true))}
          >
            <Image src={hamburgerMenu} width={24} height={24} alt="" />
          </span>
          <div
            className={cn(
              'v2-relative v2-flex v2-items-center sm:v2-w-[250px] xs:v2-w-[250px] md:v2-w-[25rem] lg:v2-w-[25rem]',
              {
                'v2-w-full sm:v2-w-full xs:v2-w-full md:v2-w-full lg:v2-w-full !v2-relative md:!v2-relative sm:!v2-absolute xs:!v2-absolute v2-left-0 v2-px-0 md:v2-px-0 sm:v2-px-[14px] xs:v2-px-[14px]':
                  isSearchBarFocused,
              }
            )}
          >
            <TextInput
              {...inputProps}
              id="search-from-header"
              type="text"
              placeholder="Product Search"
              value={value}
              ref={searchBarInputRef as any}
              inputClassName={cn(
                'v2-pr-[64px] sm:v2-h-[34px] v2-h-[34px] md:v2-h-[34px] v2-pt-[4px] v2-pb-[6px] v2-text-sm v2-rounded-[4px] v2-pl-[20px]',
                { 'v2-w-full': isSearchBarFocused }
              )}
              onChange={e => {
                onSearchChange(e);
              }}
              onClick={e => onClick(e as unknown as MouseEvent)}
              onFocus={e => {
                setSearchBarFocused(true);
                if (!isSearchBarFocused) onFocus(e as unknown as Event);
                if (value.length > 0) {
                  const target = { value };
                  const event = { target, currentTarget: target, relatedTarget: target };

                  onChange(event as unknown as Event);
                }
              }}
              onKeyDown={e => {
                onKeyDown(e as unknown as KeyboardEvent);
                if (e.key === 'Enter') {
                  if (!value) return;
                  onSearch();
                  closeAutoCompleteMenu();
                  setSearchBarFocused(false);
                  // addSearchQueryToRecentSearches(value);
                }
              }}
              onCompositionEnd={e => onCompositionEnd(e as unknown as Event)}
            />
            <Button
              title="search"
              type="button"
              className={cn(
                'v2-absolute v2-right-0 v2-w-[34px] !v2-h-[34px] sm:!v2-h-[34px] sm:!v2-w-[34px] !v2-p-0 v2-z-[2] !v2-rounded-xs v2-flex v2-items-center v2-justify-center hover:v2-bg-primary hover:v2-text-white !v2-rounded-l-none',
                {
                  'v2-right-0 md:v2-right-0 sm:v2-right-[14px] xs:v2-right-[14px]': isSearchBarFocused,
                }
              )}
              scheme="primary"
              variant="filled"
              onClick={() => {
                if (!value) {
                  try {
                    document.getElementById('search-from-header')?.focus();
                  } catch (error) {
                    console.error(error);
                  }
                  return setSearchBarFocused(true);
                }
                onSearch();
                closeAutoCompleteMenu();
                // addSearchQueryToRecentSearches(value);
              }}
              btnSize="sm"
            >
              {search?.isLoading ? <LoadingIcon /> : <SearchOutlined className="!v2-text-base sm:!v2-text-xl" />}
            </Button>
            {isSearchBarFocused && <AutoCompleteWrapper />}
          </div>
        </div>
      </Container>
    </div>
  );
}
